<template>
  <InputWrapper
    v-slot="{ ariaProps, inputClasses, inputId, invalid }"
    v-tooltip.top="{
      value: t('rateShop.charges.selectionRequired'),
      disabled: !disabled,
    }"
    :errors="allErrors"
    :input-id="id"
    :label="t('rateShop.charges.margin')"
    label-class="visually-hidden"
    class="inputgroup-container"
  >
    <InputGroup class="edit-quote-charge-uplift-rate-and-unit-inputgroup">
      <EditQuoteChargeRateInputGroup
        :id="inputId"
        v-model="rate"
        v-bind="ariaProps"
        input-group-class="no-right-radius"
        :class="inputClasses"
        :invalid="invalid"
        :currency="inputCurrency"
        :disabled="disabled"
        :mode="inputMode"
        :max-fraction-digits="maxPercentFractionDigits"
      />

      <InputGroupAddon>
        <RygenDropdown
          :id="`${inputId}-unit`"
          v-model="unit"
          :append-to="appendTo"
          :aria-label="t('rateShop.charges.unit')"
          :options="upliftUnitOptions"
          :disabled="disabled"
          option-label="label"
          option-value="value"
        />
      </InputGroupAddon>
    </InputGroup>
  </InputWrapper>
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import type { PropType, Ref } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTooltip } from '@rygen/rygen-ui/composables';
import { Currency, DropdownOption } from '@rygen/rygen-ui/model';

import { RateQuoteChargeUpliftRuleUnit } from '@/model';

import EditQuoteChargeRateInputGroup from '@/components/rateShop/editQuote/EditQuoteChargeRateInputGroup.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';

const rate = defineModel({
  type: Number,
  default: () => 0,
});

const unit = defineModel('unit', {
  type: String as PropType<RateQuoteChargeUpliftRuleUnit>,
  default: () => RateQuoteChargeUpliftRuleUnit.FLAT,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  currency: {
    type: String as PropType<Currency>,
    default: () => Currency.USD,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  unitErrors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: 'self',
  },
});
const { id, currency, errors, unitErrors, appendTo } = toRefs(props);

const { t } = useI18n();
const vTooltip = useTooltip();

const upliftUnitOptions: Ref<DropdownOption<string>[]> = computed(() => [
  {
    label: '%',
    value: RateQuoteChargeUpliftRuleUnit.PERCENT,
  },
  {
    label: t('rateShop.charges.flat'),
    value: RateQuoteChargeUpliftRuleUnit.FLAT,
  },
]);

const inputMode = computed(() =>
  unit.value === 'FLAT' ? 'currency' : 'decimal',
);

const inputCurrency = computed(() =>
  unit.value === 'FLAT' ? currency.value : undefined,
);

const maxPercentFractionDigits = computed(() =>
  unit.value === RateQuoteChargeUpliftRuleUnit.PERCENT ? 6 : undefined,
);

const allErrors: Ref<ErrorObject[]> = computed(() =>
  errors.value.concat(unitErrors.value),
);
</script>

<style>
.edit-quote-charge-uplift-rate-and-unit-inputgroup.p-inputgroup {
  /* first slot (InputMoney) gets 60% of width */
  & > *:first-child {
    flex: 3;

    .p-inputnumber {
      .p-inputtext {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }

  /* second slot (DropDown) gets remaining 40% of width */
  & > *:first-child + * {
    flex: 2;
  }

  .p-inputgroupaddon {
    padding: 0;

    &:last-child {
      min-width: 40px;
    }

    .p-select {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-color: transparent;

      &:not(.p-disabled).p-focus {
        background-color: var(--input-filled-bg) !important;
      }
    }
  }
}
</style>
